import { useState, useCallback } from 'react';
import { Container } from '@/components/Container';
import { createStorage } from '@/lib/storage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';

/* ═══════════════════════════════════════════
   NEWSLETTER — Captura de email funcional
   Persiste no localStorage, integração futura
   com serviço de email marketing
   ═══════════════════════════════════════════ */

const newsletterStorage = createStorage<string[]>('newsletter-emails', []);

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function NewsletterSection() {
  const { t } = useTranslation();
  const { trackNewsletter } = useAnalytics();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const submit = useCallback(() => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setStatus('error');
      setMessage(t('home.newsletter.errorEmpty'));
      return;
    }
    if (!isValidEmail(trimmed)) {
      setStatus('error');
      setMessage(t('home.newsletter.errorInvalid'));
      return;
    }

    const existing = newsletterStorage.get();
    if (existing.includes(trimmed)) {
      setStatus('success');
      setMessage(t('home.newsletter.alreadySubscribed'));
      return;
    }

    newsletterStorage.set([...existing, trimmed]);
    setStatus('success');
    setMessage(t('home.newsletter.successMessage'));
    setEmail('');
    trackNewsletter('home-section');

    // TODO: enviar para API de email marketing (Mailchimp, ConvertKit, etc.)
  }, [email, t, trackNewsletter]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') submit();
    },
    [submit]
  );

  return (
    <section className="py-16 sm:py-20 bg-teal-deep">
      <Container className="max-w-2xl uw:max-w-2xl suw:max-w-2xl text-center">
        <span className="font-body text-sm text-teal-200 font-semibold uppercase tracking-wider">
          {t('home.newsletter.overline')}
        </span>
        <h2 className="font-display text-4xl sm:text-5xl text-white mt-2 mb-4">
          {t('home.newsletter.title')}
        </h2>
        <p className="font-body text-white/80 mb-8">
          {t('home.newsletter.descriptionLine1')}
          <br className="hidden sm:block" />
          {t('home.newsletter.descriptionLine2')}
        </p>

        {status === 'success' ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6">
            <div className="text-4xl mb-3">📬</div>
            <p className="font-body text-lg text-white font-semibold">{message}</p>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (status === 'error') setStatus('idle');
              }}
              onKeyDown={handleKeyDown}
              placeholder="seu@email.com"
              className="flex-1 px-5 py-3.5 bg-white/10 border border-white/20 rounded-xl font-body text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/15 focus:ring-2 focus:ring-white/20 transition-all"
            />
            <button
              onClick={submit}
              className="px-8 py-3.5 bg-coral hover:bg-[#c96a52] text-white font-body font-bold rounded-xl transition-colors uppercase tracking-wide whitespace-nowrap"
            >
              {t('home.newsletter.button')}
            </button>
          </div>
        )}

        {status === 'error' && <p className="font-body text-sm text-coral mt-3">{message}</p>}

        <p className="font-body text-white/40 text-xs mt-4">{t('home.newsletter.privacy')}</p>
      </Container>
    </section>
  );
}
